import { useRef, useState, useEffect } from "react";
import { motion, useInView } from "framer-motion";
import * as Lucide from "lucide-react";
import { cn } from "@/lib/utils";

// Resolve a lucide icon by name string
export function Icon({ name, className, ...props }) {
  const Cmp = Lucide[name] || Lucide.Circle;
  return <Cmp className={className} strokeWidth={1.6} {...props} />;
}

export const easeExpo = [0.16, 1, 0.3, 1];

// Scroll reveal wrapper
export function Reveal({ children, className, delay = 0, y = 40, once = true }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once, margin: "-80px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, ease: easeExpo, delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

// Staggered container + item helpers
export const staggerContainer = {
  hidden: {},
  show: { transition: { staggerChildren: 0.09 } },
};
export const staggerItem = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: easeExpo } },
};

// Eyebrow / section heading
export function SectionHeading({ overline, title, subtitle, align = "left", className }) {
  return (
    <div className={cn(align === "center" && "text-center mx-auto max-w-2xl", "max-w-3xl", className)}>
      {overline && (
        <div className={cn("mb-4 flex items-center gap-2", align === "center" && "justify-center")}>
          <span className="h-px w-8 bg-primary" />
          <span className="text-xs font-bold uppercase tracking-[0.22em] text-primary">{overline}</span>
        </div>
      )}
      <h2 className="font-display text-4xl md:text-5xl font-medium tracking-tight leading-[1.05] text-balance">{title}</h2>
      {subtitle && <p className="mt-5 text-base md:text-lg text-muted-foreground leading-relaxed">{subtitle}</p>}
    </div>
  );
}

// Animated counter
export function Counter({ value, suffix = "", duration = 1.8, className }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [display, setDisplay] = useState(0);
  useEffect(() => {
    if (!inView) return;
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / (duration * 1000), 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(Math.round(eased * value));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, duration]);
  return (
    <span ref={ref} className={className}>
      {display}
      {suffix}
    </span>
  );
}

// Magnetic hover wrapper (for buttons)
export function Magnetic({ children, className, strength = 0.35 }) {
  const ref = useRef(null);
  const handleMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - (r.left + r.width / 2)) * strength;
    const y = (e.clientY - (r.top + r.height / 2)) * strength;
    el.style.transform = `translate(${x}px, ${y}px)`;
  };
  const reset = () => {
    if (ref.current) ref.current.style.transform = "translate(0px, 0px)";
  };
  return (
    <div
      ref={ref}
      onMouseMove={handleMove}
      onMouseLeave={reset}
      className={cn("inline-block will-change-transform transition-transform duration-300 ease-expo", className)}
    >
      {children}
    </div>
  );
}

// Infinite marquee
export function Marquee({ items, className, itemClassName, duration = 40 }) {
  const list = [...items, ...items];
  return (
    <div className={cn("marquee-pause overflow-hidden", className)}>
      <div className="flex w-max animate-marquee" style={{ "--marquee-duration": `${duration}s` }}>
        {list.map((it, i) => (
          <span key={i} className={cn("flex items-center", itemClassName)}>
            {it}
          </span>
        ))}
      </div>
    </div>
  );
}
